/**
 * Motor de cálculo de impostos — fonte única de verdade para backend e frontend.
 * Fórmula validada contra a planilha original (especificação §3.3) e idêntica à
 * do protótipo `update/central-propostas_1.html`. NÃO alterar sem revalidar.
 */

/**
 * A base de cálculo do ICMS é o **subtotal da linha — produto + IPI**, como na
 * coluna "Base Icms" da planilha ("Sub Total"), nunca o produto sozinho. FCP e
 * DIFAL incidem sobre esse valor com o IPI incluído.
 *
 * @param {number} baseICMS  base de cálculo do ICMS (produto × quantidade + IPI)
 * @param {{alInter:number, alDest:number, fcp:number}} estado
 * @param {'dentro'|'fora'} tipo
 * @returns {{difal:number, fcp:number}}
 */
export function calcImpostos(baseICMS, estado, tipo) {
  if (!estado) return { difal: 0, fcp: 0 };
  const fcp = estado.fcp > 0 ? baseICMS * estado.fcp : 0;
  let difal;
  if (tipo === 'dentro') {
    if (estado.alDest > 0 && estado.alDest < 1) {
      const vlIcmsInter = baseICMS * estado.alInter;
      const bcMajorado = (baseICMS - vlIcmsInter) / (1 - estado.alDest);
      difal = bcMajorado * estado.alDest - vlIcmsInter;
    } else {
      difal = 0;
    }
  } else {
    difal = (baseICMS + fcp) * (estado.alDest - estado.alInter);
  }
  return { difal, fcp };
}

/**
 * Calcula uma linha da proposta.
 * @param {{unitValue:number, ipi:number, qty:number}} input
 */
export function calcLinha({ unitValue, ipi, qty }, estado, tipo) {
  const base = unitValue * qty;
  const ipiValue = base * (ipi || 0);
  const lineTotal = base + ipiValue;
  const { difal, fcp } = calcImpostos(lineTotal, estado, tipo);
  return { base, ipiValue, difal, fcp, lineTotal };
}

/**
 * Calcula a proposta inteira.
 * @param {Array<{unitValue:number, ipi:number, qty:number}>} itens
 * @param {{alInter:number, alDest:number, fcp:number}|null} estado
 * @param {'dentro'|'fora'} tipo
 */
export function calcProposta(itens, estado, tipo) {
  const linhas = itens.map((it) => ({ ...it, ...calcLinha(it, estado, tipo) }));
  const totals = linhas.reduce(
    (acc, l) => ({
      subtotal: acc.subtotal + l.lineTotal,
      totalIPI: acc.totalIPI + l.ipiValue,
      totalDifal: acc.totalDifal + l.difal,
      totalFCP: acc.totalFCP + l.fcp,
    }),
    { subtotal: 0, totalIPI: 0, totalDifal: 0, totalFCP: 0 }
  );
  return {
    linhas,
    ...totals,
    totalGeral: totals.subtotal + totals.totalDifal + totals.totalFCP,
  };
}

/** Arredonda para 2 casas ao persistir/exibir valores monetários. */
export const money = (n) => Math.round((Number(n) || 0) * 100) / 100;
